import { useEffect, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Building2, Check, Loader2, Plus, RefreshCw, Send, X } from 'lucide-react';
import { PALETTE } from '@/types';
import { cn } from '@/utils/cn';
import { DEFAULT_TZ_OFFSET_MIN, TZ_OFFSETS, tzOffsetLabel } from '@/utils/timezones';
import { fetchUnlinkedCities, linkBranch, type UnlinkedCity } from '@/data/branchesClient';

/**
 * Экран «Филиалы»: заказчик сам подключает новый город (заведённый в Битриксе)
 * и привязывает к филиалу рабочую группу Telegram — без программиста.
 */
export function BranchesPage() {
  const { branches, setModal, reload, toast } = useStore();

  // Города из Битрикса, которых ещё нет в календаре.
  const [unlinked, setUnlinked] = useState<UnlinkedCity[]>([]);
  const [loading, setLoading] = useState(true);
  // Какой город сейчас подключаем (открыта форма).
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const loadUnlinked = async () => {
    setLoading(true);
    try {
      setUnlinked(await fetchUnlinkedCities());
    } catch {
      // Нет связи с сервером (или локальный режим без backend) — просто не показываем блок.
      setUnlinked([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadUnlinked(); }, []);

  const onLinked = async (name: string, dealsFixed: number) => {
    setLinkingId(null);
    await reload();          // филиал должен появиться в списке и в выборе города
    await loadUnlinked();    // город из Битрикса ушёл из «новых»
    toast({
      kind: 'success',
      text: dealsFixed > 0
        ? `Филиал «${name}» подключён. Игр восстановлено: ${dealsFixed}`
        : `Филиал «${name}» подключён`,
    });
  };

  return (
    <div className="h-full overflow-y-auto bg-surface-0">
      <div className="max-w-[920px] mx-auto p-4 sm:p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-ink-1 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-accent" /> Филиалы
          </h1>
          <button className="btn-ghost" onClick={() => void loadUnlinked()} disabled={loading} title="Проверить, не появились ли новые города в Битриксе">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Обновить
          </button>
        </div>

        {/* Новые города из Битрикса — блок появляется, только если есть что подключать. */}
        {unlinked.length > 0 && (
          <div className="panel p-5 space-y-3 border-accent/40">
            <div>
              <h2 className="text-sm font-semibold text-ink-1">Новые города из Битрикса</h2>
              <p className="text-[13px] text-ink-3 mt-0.5">
                В Битриксе есть города, которых нет в календаре. Подключите их — игры этих городов встанут в календарь.
              </p>
            </div>
            <div className="space-y-2">
              {unlinked.map((c) => (
                <div key={c.enum_id} className="rounded-xl border border-line bg-surface-2/40 p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0 text-sm font-semibold text-ink-1 truncate">{c.name}</div>
                    {linkingId === c.enum_id ? (
                      <button className="btn-ghost" onClick={() => setLinkingId(null)}>
                        <X className="w-4 h-4" /> Отмена
                      </button>
                    ) : (
                      <button className="btn-primary" onClick={() => setLinkingId(c.enum_id)}>
                        <Plus className="w-4 h-4" /> Подключить
                      </button>
                    )}
                  </div>
                  {linkingId === c.enum_id && (
                    <LinkForm city={c} takenColors={branches.map((b) => b.color)} onDone={onLinked} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Список подключённых филиалов. */}
        <div className="space-y-2">
          {branches.length === 0 ? (
            <div className="panel p-6 text-center text-sm text-ink-3">
              Филиалов пока нет. Они появятся здесь, когда вы подключите город из Битрикса.
            </div>
          ) : (
            branches.map((b) => {
              return (
                <button
                  key={b.id}
                  onClick={() => setModal({ kind: 'branch', id: b.id })}
                  className="panel hover:border-accent w-full p-3.5 flex items-stretch gap-3 text-left transition-colors"
                >
                  <span className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                  {/* На телефоне название и статусы в столбик (иначе бейджи выдавливали имя
                      филиала до нуля и обрезались справа), на широком экране — в строку. */}
                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink-1 truncate">{b.name}</div>
                      <div className="text-[12px] text-ink-3 truncate">
                        {[b.city, tzOffsetLabel(b.tz_offset_minutes)].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 sm:justify-end">
                      <Indicator ok={!!b.bitrix_enum_id}
                        okText="Битрикс: подключён" badText="Битрикс: не связан" />
                      <Indicator ok={!!b.telegram_linked} icon={Send}
                        okText="Группа привязана" badText="Группа не привязана" />
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/** Двухцветный индикатор состояния (зелёный — хорошо, серый — надо доделать). */
function Indicator({ ok, okText, badText, icon: Icon }: {
  ok: boolean; okText: string; badText: string; icon?: typeof Send;
}) {
  return (
    <span className={cn('chip flex-shrink-0 whitespace-nowrap', ok ? '!text-status-paid !bg-status-paid/12' : '!text-ink-3 !bg-ink-3/10')}>
      {Icon ? <Icon className="w-3 h-3" /> : ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {ok ? okText : badText}
    </span>
  );
}

/** Форма подключения города: имя (можно поправить), часовой пояс, цвет. */
function LinkForm({ city, takenColors, onDone }: {
  city: UnlinkedCity;
  takenColors: string[];
  onDone: (name: string, dealsFixed: number) => void | Promise<void>;
}) {
  const toast = useStore((s) => s.toast);
  const [name, setName] = useState(city.name);
  // По умолчанию UTC+5 — так живёт почти вся сеть (кроме Баку и Бишкека).
  const [tzOffsetMin, setTzOffsetMin] = useState(DEFAULT_TZ_OFFSET_MIN);
  // Цвет по умолчанию — следующий свободный из палитры, чтобы города не сливались.
  const [color, setColor] = useState(PALETTE[takenColors.length % PALETTE.length]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    try {
      const res = await linkBranch({ enum_id: city.enum_id, name: name.trim(), tz_offset_minutes: tzOffsetMin, color });
      await onDone(res.branch?.name ?? name.trim(), res.deals_fixed ?? 0);
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось подключить филиал' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-line space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="field-label">Название филиала</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus disabled={busy} />
        </div>
        <div>
          <label className="field-label">Часовой пояс</label>
          <select className="input" value={String(tzOffsetMin)} onChange={(e) => setTzOffsetMin(Number(e.target.value))} disabled={busy}>
            {TZ_OFFSETS.map((t) => <option key={t.offsetMin} value={t.offsetMin}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="field-label">Цвет</label>
        <div className="flex flex-wrap gap-1.5">
          {PALETTE.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)} disabled={busy}
              className={cn('w-7 h-7 rounded-full', color.toLowerCase() === c.toLowerCase() && 'ring-2 ring-ink-1 ring-offset-2')}
              style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>
      <button className="btn-primary" onClick={() => void submit()} disabled={busy || !name.trim()}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Подключить
      </button>
    </div>
  );
}
